import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import error from "../../utils/error";
import BackupAPIServices, { IBackupInfo } from "../../services/backupApi";

interface IConnectionPageState {
  createBackup: {
    isSaving: boolean,
    isDeleting: boolean,
    error?: string,
    responseData? : any[]
  },
  backupList: {
    isLoading: boolean,
    status: string,
    data: IBackupInfo[],
    error?: string
  },
  backupDetails: {
    isLoading: boolean,
    status: string,
    data: IBackupInfo,
    error?: string
  },
  runOnDemandBackup: {
    isSaving: boolean,
    isDeleting: boolean,
    error?: string,
    responseData? : any[]
  },
}

const initialBackupState: IConnectionPageState = {
  createBackup: {
    isSaving: false,
    isDeleting: false,
    error: undefined,
    responseData: [],
  },
  backupList: {
    isLoading: false,
    status: "",
    data: [],
    error: undefined
  },
  backupDetails: {
    isLoading: false,
    status: "",
    data: {
      id: "",
      db_connection_id: "",
      db_tool_id: "",
      compress_file_format_id: "",
      database_name: "",
      retention: 20,
    },
    error: undefined
  },
  runOnDemandBackup: {
    isSaving: false,
    isDeleting: false,
    error: undefined,
    responseData: [],
  },
};

export const createBackup = createAsyncThunk(
  "backup/createBackup",
  async (payload: IBackupInfo) => {
    try {
      return BackupAPIServices.createBackup(payload);
    } catch (error_) {
      return error_;
    }
  }
);

export const getBackupList = createAsyncThunk(
  "backup/getBackupList",
  async () => {
    try {
      return BackupAPIServices.getBackupList();
    } catch (error_) {
      return error_;
    }
  }
);

export const getBackupDetailsById = createAsyncThunk(
  "backup/getBackupDetailsById",
  async (payload: {backup_id: string}) => {
    try {
      return BackupAPIServices.getBackupDetailsById(payload);
    } catch (error_) {
      return error_;
    }
  }
);

export const runOnDemandBackUp = createAsyncThunk(
  "backup/runOnDemandBackUp",
  async (payload: {id: string}) => {
    try {
      return BackupAPIServices.runOnDemandBackUp(payload);
    } catch (error_) {
      return error_;
    }
  }
);

const backupSlice = createSlice({
  name: "backup",
  initialState: initialBackupState,
  reducers: {},
  extraReducers(builder): void {
    // Create backup
    builder.addCase(createBackup.pending, (state) => {
      state.createBackup.isSaving = true;
    });
    builder.addCase(createBackup.fulfilled, (state, action) => {
      state.createBackup.isSaving = false;
      state.createBackup.responseData = action.payload;
    });
    builder.addCase(createBackup.rejected, (state, action) => {
      state.createBackup.isSaving = false;
      state.createBackup.error = error(action.payload);
    });

    // Backup list
    builder.addCase(getBackupList.pending, (state) => {
      state.backupList.isLoading = true;
    });
    builder.addCase(getBackupList.fulfilled, (state, action) => {
      state.backupList.isLoading = false;
      state.backupList.data = action.payload;
    });
    builder.addCase(getBackupList.rejected, (state, action) => {
      state.backupList.isLoading = false;
      state.backupList.error = error(action.payload);
    });

    // Backup details
    builder.addCase(getBackupDetailsById.pending, (state) => {
      state.backupDetails.isLoading = true;
    });
    builder.addCase(getBackupDetailsById.fulfilled, (state, action) => {
      state.backupDetails.isLoading = false;
      state.backupDetails.data = action.payload;
    });
    builder.addCase(getBackupDetailsById.rejected, (state, action) => {
      state.backupDetails.isLoading = false;
      state.backupDetails.error = error(action.payload);
    });

    // Run onDemand backup
    builder.addCase(runOnDemandBackUp.pending, (state) => {
      state.runOnDemandBackup.isSaving = true;
    });
    builder.addCase(runOnDemandBackUp.fulfilled, (state, action) => {
      state.runOnDemandBackup.isSaving = false;
      state.runOnDemandBackup.responseData = action.payload;
    });
    builder.addCase(runOnDemandBackUp.rejected, (state, action) => {
      state.runOnDemandBackup.isSaving = false;
      state.runOnDemandBackup.error = error(action.payload);
    });
  },
});

export default backupSlice.reducer;
